import { useEffect, useState } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  importAttendance, loadAttendanceDay, loadAttendanceSummary, saveAttendance,
  type AttDay, type AttStatus, type AttSummary,
} from "../data";
import { downloadText, parseCsv, toCsv } from "../lib/csv";
import type { ImportResult, Workspace } from "../types";

const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const tone: Record<AttStatus, string> = { present: "success", late: "warning", on_leave: "info", absent: "danger", day_off: "neutral", upcoming: "neutral" };
const hhmm = (t: string | null) => (t ? t.slice(0, 5) : "");

type Tab = "day" | "summary" | "import";

export function Attendance({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t } = useI18n();
  const canManage = ws.permissions.has("attendance.manage");
  const [tab, setTab] = useState<Tab>("day");
  const tabs: Tab[] = canManage ? ["day", "summary", "import"] : ["day", "summary"];
  const label: Record<Tab, StringKey> = { day: "tabDay", summary: "tabSummary", import: "tabImport" };
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>
            {t(label[k])}
          </button>
        ))}
      </div>
      {tab === "day" && <Day ws={ws} onOpen={onOpen} />}
      {tab === "summary" && <Summary ws={ws} onOpen={onOpen} />}
      {tab === "import" && <Import ws={ws} />}
    </div>
  );
}

function Day({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const canManage = ws.permissions.has("attendance.manage");
  const [day, setDay] = useState(iso(new Date()));
  const [rows, setRows] = useState<AttDay[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [times, setTimes] = useState({ in: "", out: "" });
  const [error, setError] = useState<string | null>(null);

  const refresh = () => loadAttendanceDay(companyId, day).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { setRows(null); void refresh(); }, [companyId, day]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(r: AttDay) {
    setError(null);
    try { await saveAttendance(companyId, r.employment_id, day, times.in, times.out); setEditing(null); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
  }

  const count = (s: AttStatus) => (rows ?? []).filter((r) => r.status === s).length;
  const shift = (n: number) => { const d = new Date(day); d.setDate(d.getDate() + n); setDay(iso(d)); };

  return (
    <>
      <div className="row">
        <button type="button" className="btn secondary small" onClick={() => shift(-1)} aria-label="Previous day">‹</button>
        <input id="att-day" type="date" className="date-input" value={day} onChange={(e) => setDay(e.target.value)} />
        <button type="button" className="btn secondary small" onClick={() => shift(1)} aria-label="Next day">›</button>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows && rows.length > 0 && (
        <div className="stats">
          {(["present", "late", "on_leave", "absent"] as AttStatus[]).map((s) => (
            <div className="stat" key={s}><span className={`stat-value${s === "absent" && count(s) > 0 ? " danger-text" : ""}`}>{count(s)}</span><span className="stat-label">{t(`att_${s}` as StringKey)}</span></div>
          ))}
        </div>
      )}
      {rows === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("noEmployeesDay")}</p> : (
        <section className="card flush">
          <div className="table-wrap">
            <table>
              <thead><tr><th>{t("employee")}</th><th>{t("status")}</th><th>{t("checkIn")}</th><th>{t("checkOut")}</th><th className="hide-sm">{t("minutesLate")}</th>{canManage && <th />}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employment_id}>
                    <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{pick(r.full_name, r.full_name_ar)}</button></td>
                    <td><span className={`badge ${tone[r.status]}`}>{t(`att_${r.status}` as StringKey)}</span></td>
                    {editing === r.employment_id ? (
                      <>
                        <td><input id={`in-${r.employment_id}`} type="time" className="time-input" value={times.in} onChange={(e) => setTimes({ ...times, in: e.target.value })} aria-label={t("checkIn")} /></td>
                        <td><input id={`out-${r.employment_id}`} type="time" className="time-input" value={times.out} onChange={(e) => setTimes({ ...times, out: e.target.value })} aria-label={t("checkOut")} /></td>
                        <td className="hide-sm" />
                        <td className="cell-actions"><div className="row">
                          <button type="button" className="btn secondary small" onClick={() => setEditing(null)}>{t("cancel")}</button>
                          <button type="button" className="btn primary small" onClick={() => save(r)}>{t("save")}</button>
                        </div></td>
                      </>
                    ) : (
                      <>
                        <td className="mono">{hhmm(r.check_in) || "—"}</td>
                        <td className="mono">{hhmm(r.check_out) || "—"}</td>
                        <td className="mono hide-sm">{r.minutes_late || ""}</td>
                        {canManage && (
                          <td className="cell-actions">
                            {r.status !== "day_off" && r.status !== "upcoming" && (
                              <button type="button" className="btn secondary small" onClick={() => { setEditing(r.employment_id); setTimes({ in: hhmm(r.check_in), out: hhmm(r.check_out) }); }}>{t("edit")}</button>
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function Summary({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t, pick } = useI18n();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [rows, setRows] = useState<AttSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const [y, m] = month.split("-").map(Number);
    const from = `${month}-01`;
    const to = iso(new Date(y, m, 0));
    setRows(null);
    loadAttendanceSummary(ws.membership.company_id, from, to).then(setRows).catch((e) => setError(e.message));
  }, [ws.membership.company_id, month]);

  function exportCsv() {
    if (!rows) return;
    downloadText(`attendance-${month}.csv`, toCsv(
      ["employee_number", "name", "department", "working_days", "present", "late_days", "late_minutes", "leave", "absent", "missing_checkout"],
      rows.map((r) => [r.employee_number, r.full_name, r.department_name ?? "", String(r.working_days), String(r.present_days), String(r.late_days),
        String(r.late_minutes), String(r.leave_days), String(r.absent_days), String(r.missing_checkout)]),
    ));
  }

  return (
    <>
      <div className="row">
        <input id="att-month" type="month" className="date-input" value={month} onChange={(e) => setMonth(e.target.value)} aria-label={t("month")} />
        <button type="button" className="btn secondary small" onClick={exportCsv} disabled={!rows || rows.length === 0}>CSV</button>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows === null ? <p className="muted">{t("loading")}</p> : (
        <section className="card flush">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>{t("employee")}</th><th>{t("workingDays")}</th><th>{t("presentDays")}</th><th>{t("lateDays")}</th>
                <th className="hide-sm">{t("lateMinutes")}</th><th>{t("leaveDays")}</th><th>{t("absentDays")}</th><th className="hide-sm">{t("missingOut")}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employment_id}>
                    <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{pick(r.full_name, r.full_name_ar)}</button></td>
                    <td className="mono">{r.working_days}</td>
                    <td className="mono">{r.present_days}</td>
                    <td className="mono">{r.late_days || ""}</td>
                    <td className="mono hide-sm">{r.late_minutes || ""}</td>
                    <td className="mono">{r.leave_days || ""}</td>
                    <td className={`mono${r.absent_days > 0 ? " danger-text strong" : ""}`}>{r.absent_days || ""}</td>
                    <td className="mono hide-sm">{r.missing_checkout || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function Import({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<Record<string, string>[] | null>(null);
  const [kind, setKind] = useState<"import" | "device">("import");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setResults(null);
    setError(null);
    const parsed = parseCsv(await file.text());
    setKind(parsed.some((r) => r.punch) ? "device" : "import");
    setRows(parsed);
  }

  async function run() {
    if (!rows) return;
    setBusy(true);
    setError(null);
    try {
      const out: ImportResult[] = [];
      for (let i = 0; i < rows.length; i += 2000) {
        const res = await importAttendance(ws.membership.company_id, rows.slice(i, i + 2000), kind);
        out.push(...res.map((r) => ({ ...r, row: r.row + i })));
      }
      setResults(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  const bad = results?.filter((r) => !r.ok) ?? [];
  return (
    <section className="card form-card">
      <p>{t("attImportHelp1")}</p>
      <p>{t("attImportHelp2")}</p>
      <div className="row">
        <button type="button" className="btn secondary" onClick={() => downloadText("attendance-daily-template.csv",
          toCsv(["employee_number", "date", "check_in", "check_out"], [["EMP-0001", iso(new Date()), "08:55", "17:05"]]))}>{t("dailyTemplate")}</button>
        <button type="button" className="btn secondary" onClick={() => downloadText("attendance-punch-template.csv",
          toCsv(["employee_number", "punch"], [["EMP-0001", `${iso(new Date())} 08:55`], ["EMP-0001", `${iso(new Date())} 17:05`]]))}>{t("punchTemplate")}</button>
        <label className="btn primary file-btn">{t("chooseFile")}
          <input id="att-file" type="file" accept=".csv,text/csv" onChange={(e) => void pick(e.target.files?.[0])} /></label>
      </div>
      {rows && <p className="muted"><strong>{rows.length}</strong> {t("rowsFound")}</p>}
      {error && <p className="alert danger" role="alert">{error}</p>}
      {results && (
        <div className="stack">
          <div className="row">
            <span className="badge success">{results.length - bad.length} {t("imported")}</span>
            {bad.length > 0 && <span className="badge danger">{bad.length} {t("failed")}</span>}
          </div>
          {bad.length > 0 && (
            <div className="table-wrap"><table>
              <thead><tr><th>{t("row")}</th><th>{t("error")}</th></tr></thead>
              <tbody>{bad.slice(0, 200).map((r) => <tr key={r.row}><td className="mono">{r.row + 1}</td><td className="danger-text">{r.error}</td></tr>)}</tbody>
            </table></div>
          )}
        </div>
      )}
      <div className="actions">
        <button type="button" className="btn primary" disabled={!rows || rows.length === 0 || busy || results !== null} onClick={run}>
          {busy ? t("importing") : t("runImport")}
        </button>
      </div>
    </section>
  );
}
